import { useMutation } from '@tanstack/react-query'
import { MutationOptions } from 'models/general'
import { IInvoice, InvoiceFiles } from 'models/invoices.models'
import { downloadFileFromData } from 'lib/helpers/utilities'
import useDialog from 'hooks/Shared/useDialog'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import { getCfdiFilesService } from 'services/Invoices/invoices.services'
import useLoadingAdditionalInformation from 'hooks/Shared/useLoadingAdditionalInformation'

export interface IUseDownloadInvoiceFilesVariables {
  activeWorkspace: number
  activeInvoice: IInvoice
}

export const useDownloadInvoiceFiles = (
  options: MutationOptions<
    InvoiceFiles,
    any,
    IUseDownloadInvoiceFilesVariables
  > = {}
) => {
  const dialog = useDialog()

  const showAdditionalInformationLoading = useLoadingAdditionalInformation()

  const { onSuccess, onError, noDialog } = options

  const mutation = useMutation<
    InvoiceFiles,
    any,
    IUseDownloadInvoiceFilesVariables
  >(
    ({ activeWorkspace, activeInvoice }) => {
      if (!activeInvoice) throw new Error('CFDI cargado incorrectamente')
      return getCfdiFilesService(activeWorkspace, activeInvoice)
    },
    {
      onMutate: () => {
        showAdditionalInformationLoading()
      },
      onSuccess: (files, variables, ctx) => {
        dialog.close()
        const { activeWorkspace, activeInvoice } = variables
        onSuccess && onSuccess(files, { activeWorkspace, activeInvoice }, ctx)
        downloadFileFromData(
          `${activeInvoice.uuid ?? 'XXXX0000-0000-XXXX-0000-XXXX0000XXXX'}.pdf`,
          files.pdf
        )
        downloadFileFromData(
          `${activeInvoice.uuid ?? 'XXXX0000-0000-XXXX-0000-XXXX0000XXXX'}.xml`,
          files.xml
        )
      },
      onError: async (error, variables, ctx) => {
        setTimeout(async () => {
          const op = selectErrorDialog(error)
          if (op?.variant) {
            await dialog[op.variant](op)
            return
          }

          if (!noDialog) {
            const rp = await dialog.dangerAsync({
              subtitle: 'No se pudo descargar el CFDI seleccionado.',
              primaryButtonText: 'Reintentar',
              secondaryButtonText: 'Cancelar',
            })

            if (rp) {
              mutation.mutate(variables)
              return
            }
          }

          onError && onError(error, variables, ctx)
        }, 600)
      },
    }
  )

  return mutation
}

export default useDownloadInvoiceFiles
